import { getBooking } from "../../../app/api/getBooking";
import { BaseAction } from "../../../core/actions/BaseAction";
import { _eventBus } from "../../../core/events/EventBus";
import { determinePassengerType } from "../../../Lib/Utils/validationUtil";
import { updateRecoilState } from "../../../Orchestrator/Recoils/Events/Recoil";
import { IBookingResponse, IDeterminePassenger, ISelectedPassenger } from "../../../Orchestrator/Types/Index";
import { prepareBookingSummaryAction } from "../Checkout/Summary/PrepareBookingSummaryAction";

export interface IAddShuttleTransferContinueParams {
    bookingId: string;
    selectedPassengers: string[];
}

class AddShuttleTransferContinueClickAction extends BaseAction<string, IAddShuttleTransferContinueParams> {

    override async run(): Promise<string> {
        let response: string = "fail";
        try {
            localStorage.removeItem("isAddShuttleTransferFlow");
            const { bookingId, selectedPassengers } = this.params!;

            if (!bookingId || !selectedPassengers || selectedPassengers.length === 0) {
                console.warn("Missing required data for shuttle transfer continue action");
                return response;
            }

            // Get booking data
            const getBookingResponse: any = await getBooking(bookingId);
            
            if (getBookingResponse.status === 'ok' && getBookingResponse.answer) {
                const bookingResponse: IBookingResponse = getBookingResponse.answer;
                
                // Process passengers
                const passengerList: ISelectedPassenger[] = bookingResponse.booking?.passengers.map(passenger => {
                    const passengerObject: ISelectedPassenger = {
                        id: passenger.id,
                        firstName: passenger?.detail?.firstName || "",
                        lastName: passenger?.detail?.lastName || "",
                        email: passenger?.detail?.email || "",
                        mobile: "",
                        remember: false,
                        age: passenger.age,
                        seatingDetails: null,
                        cards: passenger.cards || [],
                        prmNeeds: passenger.prmNeeds
                    }
                    return passengerObject;
                });

                // Filter passengers based on selection
                const filteredPassengers = passengerList.filter(p => selectedPassengers.includes(p.id));

                // Determine passenger types and counts
                const passengersObject: { [key: string]: number } = {};
                filteredPassengers.forEach((passenger) => {
                    const determinePassenger: IDeterminePassenger = {
                        age: passenger.age || 0,
                        cards: passenger?.cards || [],
                        prmNeeds: ((passenger?.prmNeeds || []).length > 0 && passenger?.prmNeeds) ? passenger?.prmNeeds[0] : ""
                    }
                    const passengerType = determinePassengerType(determinePassenger);

                    // Initialize the count for the passenger type if not already present
                    if (!passengersObject[passengerType]) {
                        passengersObject[passengerType] = 0;
                    }

                    // Increment the count for this passenger type
                    passengersObject[passengerType] += 1;
                });

                // Update search state with passenger counts
                updateRecoilState("searchStateAtom", { passengers: passengersObject });

                // Update passenger state with filtered passengers
                const filteredBookingPassengers = bookingResponse.booking?.passengers.filter(p => selectedPassengers.includes(p.id)) || [];
                updateRecoilState("passengerStateAtom", null);
                updateRecoilState("passengerStateAtom", filteredBookingPassengers);

                const updatedBookingResponse = {
                    ...bookingResponse,
                    booking: {
                        ...bookingResponse.booking,
                        passengers: filteredBookingPassengers
                    }
                };

                updateRecoilState('bookingResponseAtom', updatedBookingResponse);
                updateRecoilState("guestCheckoutBookingIdAtom", updatedBookingResponse.booking.id);

                // Flow flags MUST be set BEFORE the summary runs. PrepareBookingSummary's
                // quick-exchange total block overwrites payment.total with
                // tickets+exchangeFee (0 for a prebooked-shuttle-only summary) unless
                // isAddShuttleFlowAtom excludes it. isQuickExchangeStateAtom is PERSISTED
                // to localStorage (30 min TTL) while isAddShuttleFlowAtom is not, so after
                // a page reload mid-flow — e.g. pasting the booking-details URL while on
                // the add-shuttle /checkout, then adding a shuttle again — QE reads true
                // and add-shuttle reads false, and the block zeroed the shuttle's price.
                // Setting them first makes the exclusion hold whatever leaked in.
                // The localStorage key deliberately stays BELOW: nothing between here
                // and there reads it, and writing it early would leave it set if this
                // action threw, mis-flagging the next unrelated payment.
                updateRecoilState("isQuickExchangeStateAtom", true);
                updateRecoilState("isAddShuttleFlowAtom", true);

                // Prepare booking summary
                prepareBookingSummaryAction.setParams({
                    ...updatedBookingResponse.booking,
                    isPrebookedOnly: true
                });
                await prepareBookingSummaryAction.execute();

                // Set payment state
                updateRecoilState("paymentStateAtom", {
                    // Left unset so <Payment> can preselect the customer's default saved
                    // card — it only preselects while no method is set (Payment.tsx).
                    method: null,
                    total: 0,
                });

                localStorage.setItem("isAddShuttleTransferFlow", "true");
                // Emit route change event to redirect to checkout
                _eventBus.emit({
                    eventName: "ROUTE_CHANGE",
                    data: "/checkout"
                });

                response = "success";
            } else {
                console.error("Failed to get booking data");
                response = "fail";
            }
        } catch (error) {
            console.error("AddShuttleTransferContinueClickAction error:", error);
            response = "fail";
        }
        
        return response;
    }
}

export const addShuttleTransferContinueClickAction = new AddShuttleTransferContinueClickAction();
